import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { type Company } from '@/types';
import { Building2, Check, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface InlineCompanyCellProps {
    company: Company | null | undefined;
    allCompanies: Array<{ id: string; name: string }>;
    onUpdate?: (companyId: string | null) => Promise<void>;
    className?: string;
    readOnly?: boolean;
}

export function InlineCompanyCell({ company, allCompanies, onUpdate, className, readOnly = false }: InlineCompanyCellProps) {
    const { t } = useTranslations();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(company?.id || null);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (isDialogOpen) {
            setSelectedCompanyId(company?.id || null);
        }
    }, [company?.id, isDialogOpen]);

    const handleSave = async () => {
        if (selectedCompanyId === (company?.id || null) || !onUpdate) {
            setIsDialogOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(selectedCompanyId);
            setIsDialogOpen(false);
            setSearch('');
        } catch (error) {
            console.error('Failed to update company:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setSelectedCompanyId(company?.id || null);
        setSearch('');
        setIsDialogOpen(false);
    };

    const MAX_VISIBLE_COMPANIES = 10;
    const normalizedSearch = search.trim().toLowerCase();
    const isSearching = normalizedSearch.length > 0;
    const matchingCompanies = isSearching ? allCompanies.filter((c) => c.name.toLowerCase().includes(normalizedSearch)) : allCompanies;
    const displayCompanies = isSearching ? matchingCompanies : matchingCompanies.slice(0, MAX_VISIBLE_COMPANIES);

    // Read-only mode: just display the company
    if (readOnly || !onUpdate) {
        if (!company) {
            return (
                <div className={cn('text-muted-foreground flex items-center gap-2', className)}>
                    <Building2 className="h-4 w-4" />
                    <span className="text-sm italic">{t('people.index.unassigned', 'Unassigned')}</span>
                </div>
            );
        }

        return (
            <div className={cn('flex items-center gap-2', className)}>
                <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="text-sm">{company.name}</span>
            </div>
        );
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsDialogOpen(true);
                    }}
                    className={cn(
                        'hover:bg-accent w-full rounded px-2 py-1.5 transition-colors',
                        'flex items-center gap-2 text-sm',
                        !company && 'text-muted-foreground',
                        className,
                    )}
                >
                    <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span className="max-w-[150px] truncate">{company?.name || t('people.index.unassigned', 'Unassigned')}</span>
                </button>
            </DialogTrigger>
            <DialogContent className="gap-0 p-0 sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle className="px-6 pt-6">{t('people.index.columns.company', 'Company')}</DialogTitle>
                </DialogHeader>
                <div className="flex max-h-[70vh] min-h-[420px] flex-col px-6 pb-4">
                    <Command shouldFilter={false} className="flex-1">
                        <CommandInput
                            placeholder={t('people.index.search_companies', 'Search companies...')}
                            value={search}
                            onValueChange={setSearch}
                            className="h-10"
                        />
                        <CommandList className="mt-2 max-h-[300px] overflow-y-auto">
                            <CommandEmpty>{t('people.index.no_companies_found', 'No companies found.')}</CommandEmpty>
                            <CommandGroup>
                                <CommandItem onSelect={() => setSelectedCompanyId(null)} className="cursor-pointer">
                                    <span>{t('people.index.unassigned', 'Unassigned')}</span>
                                    {selectedCompanyId === null && <Check className="ml-auto h-4 w-4 shrink-0" />}
                                </CommandItem>
                                {displayCompanies.map((c) => {
                                    const isSelected = selectedCompanyId === c.id;
                                    return (
                                        <CommandItem key={c.id} onSelect={() => setSelectedCompanyId(c.id)} className="cursor-pointer">
                                            <div className="flex w-full items-center gap-2">
                                                <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
                                                <span className="flex-1 truncate">{c.name}</span>
                                                {isSelected && <Check className="ml-auto h-4 w-4 shrink-0" />}
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                    {!isSearching && matchingCompanies.length > MAX_VISIBLE_COMPANIES && (
                        <p className="text-muted-foreground mt-2 text-xs">
                            {t('people.index.search_to_see_more_companies', 'Showing first 10 companies. Use search to find others.')}
                        </p>
                    )}
                    <div className="mt-4 flex justify-end gap-2 border-t pt-3">
                        <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
                            <X className="mr-2 h-4 w-4" />
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={isLoading}>
                            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                            {t('common.save', 'Save')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
